import type { EmailTemplate, EmailTemplateInput, RenderedEmailTemplate } from "@/schema/email-template"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

export function fetchEmailTemplates(context: ApiContext, options?: { scope?: "company" | "book" }) {
  const params = options?.scope ? `?scope=${options.scope}` : ""
  return apiContextFetch<{ email_templates: EmailTemplate[] }>(context, `email_templates${params}`)
}

async function uploadFiles(context: ApiContext, files: File[]): Promise<string[]> {
  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")
  const { uploadFileDirectly } = await import("@/utils/direct-upload")

  return Promise.all(files.map((file) => uploadFileDirectly(file, apiRoot, context.token)))
}

export async function createEmailTemplate(
  context: ApiContext,
  emailTemplate: EmailTemplateInput,
  options?: { scope?: "company" | "book" }
) {
  const { files, ...templateParams } = emailTemplate

  const fileSignedIds = files?.length ? await uploadFiles(context, files) : []

  const params = options?.scope ? `?scope=${options.scope}` : ""
  return apiContextFetch<{ email_template: EmailTemplate }>(context, `email_templates${params}`, {
    method: "POST",
    body: JSON.stringify({
      email_template: {
        ...templateParams,
        ...(fileSignedIds.length > 0 && { files: fileSignedIds }),
      },
    }),
  })
}

export async function updateEmailTemplate(context: ApiContext, id: string, emailTemplate: Partial<EmailTemplateInput>) {
  const { files, existing_signed_ids, ...templateParams } = emailTemplate

  const newSignedIds = files?.length ? await uploadFiles(context, files) : []
  const allFileSignedIds = [...(existing_signed_ids || []), ...newSignedIds]

  return apiContextFetch<{ email_template: EmailTemplate }>(context, `email_templates/${id}`, {
    method: "PATCH",
    body: JSON.stringify({
      email_template: {
        ...templateParams,
        files: allFileSignedIds,
      },
    }),
  })
}

export function destroyEmailTemplate(context: ApiContext, id: string) {
  return apiContextFetch<void>(context, `email_templates/${id}`, {
    method: "DELETE",
  })
}

export function cloneEmailTemplate(context: ApiContext, id: string, name: string, bookId?: string) {
  return apiContextFetch<{ email_template: EmailTemplate }>(context, `email_templates/${id}/clone`, {
    method: "POST",
    body: JSON.stringify({ clone: { name, ...(bookId && { book_id: bookId }) } }),
  })
}

export function renderEmailTemplate(context: ApiContext, emailTemplateId: string, householdId?: string) {
  return apiContextFetch<{ email_template_render: RenderedEmailTemplate }>(context, "email_template_renders", {
    method: "POST",
    body: JSON.stringify({
      email_template_id: emailTemplateId,
      ...(householdId && { household_id: householdId }),
    }),
  })
}

export interface ReplacePlaceholdersParams {
  content: string
  subjectType: "Household" | "Person"
  subjectId: string
  format?: "html" | "nodes"
}

export function replacePlaceholders(context: ApiContext, params: ReplacePlaceholdersParams) {
  return apiContextFetch<{ content: string }>(context, "lexical_replacements", {
    method: "POST",
    body: JSON.stringify({
      content: params.content,
      subject_type: params.subjectType,
      subject_id: params.subjectId,
      format: params.format,
    }),
  })
}
